import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { getRequest } from "../../services/api";
import { slugify } from "../../utils/slugify";
import "./SearchResults.css";

interface SearchResult {
  _id: string;
  title: string;
  slug: string;
  category: string;
  image: string;
  posted_by: string;
  views: number;
  likes: number;
  category_name: string;
  sub_category_name: string;
  blog_post_category_name: string;
  test_category_name?: string;
}

/* ─── Small inline icons (replace emoji for a more polished look) ──────── */
const IconEye = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const IconThumbsUp = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
    <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);
const IconSearchOff = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="7" /><line x1="21" y1="21" x2="15" y2="15" />
  </svg>
);

/* ─── Route resolver ─────────────────────────────────────────────────────
   NOTE: the test_series branch is a best-effort guess based on the
   /online-tests/test-page/{slug}/{testId} pattern used by the actual
   test-taking page elsewhere in the app — worth confirming against your
   real route config for the test-series landing page.
──────────────────────────────────────────────────────────────────────────── */
const getLink = (item: SearchResult) => {
  if (item.blog_post_category_name === "test_series") {
    return `/online-tests/${item.slug}`;
  } else if (item.blog_post_category_name === "mcqs") {
    return `/mcqs/mcqs-post/${slugify(item.category_name)}/${slugify(item.sub_category_name)}/${item.slug}`;
  } else {
    return `/blogs/blog-post/${slugify(item.category_name)}/${slugify(item.sub_category_name)}/${item.slug}`;
  }
};

/* ─── Result card ─────────────────────────────────────────────────────── */
const ResultCard: React.FC<{ item: SearchResult }> = ({ item }) => (
  <Link to={getLink(item)} className="sr-card-link">
    <div className="sr-card">
      <div className="sr-card-img-wrap">
        <img src={item.image} alt={item.title} loading="lazy" />
      </div>
      <div className="sr-card-body">
        <h3 className="sr-card-title">{item.title}</h3>
        {item.posted_by && <p className="sr-card-meta">{item.posted_by}</p>}
        <div className="sr-card-stats">
          <span><IconEye />{item.views ?? 0}</span>
          <span><IconThumbsUp />{item.likes ?? 0}</span>
        </div>
      </div>
    </div>
  </Link>
);

const SkeletonCards = () => (
  <>
    {Array.from({ length: 8 }).map((_, idx) => (
      <div className="sr-card sr-card-skeleton" key={idx}>
        <Skeleton height={140} />
        <div style={{ padding: "12px 14px" }}>
          <Skeleton height={16} width="85%" />
          <Skeleton height={12} width="45%" style={{ marginTop: 8 }} />
        </div>
      </div>
    ))}
  </>
);

const SearchResults = () => {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const keyword = queryParams.get("keyword") || "";
  const page = queryParams.get("page") || "1";

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        const response = await getRequest(`/search`, {
          keyword,
          page,
          limit: 20,
        });
        setResults(response.data.results || []);
      } catch (error) {
        console.error("Error fetching search results:", error);
      } finally {
        setLoading(false);
      }
    };
    if (keyword) fetchResults();
    else setLoading(false);
  }, [keyword, page]);

  return (
    <>
      <Helmet>
        <title>Search Results for "{keyword}" - Acadebility</title>
        <meta name="description" content={`Search results for ${keyword} on Acadebility`} />
      </Helmet>

      <div className="sr-page">
        {/* ── Hero ── */}
        <div className="sr-hero">
          <div className="sr-hero-inner">
            <nav className="sr-breadcrumb">
              <Link to="/">Home</Link>
              <span className="sr-sep">/</span>
              <span className="sr-crumb-current">Search</span>
            </nav>
            <h1 className="sr-title">
              {keyword ? <>Results for &ldquo;{keyword}&rdquo;</> : "Search"}
            </h1>
            {!loading && (
              <p className="sr-subtitle">
                {results.length} result{results.length === 1 ? "" : "s"} found
              </p>
            )}
          </div>
        </div>

        {/* ── Body: full width, no sidebar ── */}
        <div className="sr-body">
          {loading ? (
            <div className="sr-grid">
              <SkeletonCards />
            </div>
          ) : !keyword ? (
            <div className="sr-empty">
              <span className="sr-empty-icon"><IconSearchOff /></span>
              <h2>Search for something</h2>
              <p>Use the search bar above to find tests, MCQs, and blog posts.</p>
            </div>
          ) : results.length === 0 ? (
            <div className="sr-empty">
              <span className="sr-empty-icon"><IconSearchOff /></span>
              <h2>No results for &ldquo;{keyword}&rdquo;</h2>
              <p>Try a different keyword, or check the spelling.</p>
              <Link to="/" className="sr-empty-cta">Back to Home</Link>
            </div>
          ) : (
            <div className="sr-grid">
              {results.map(item => (
                <ResultCard item={item} key={item._id} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchResults;